/**
 * Scoped contrast lift for the two branded surfaces that read poorly when a
 * campaign ships a very dark `primary` (e.g. #0d183e navy):
 *   - playlist-item borders  (driven by --row-bg, itself a dark mix of accent)
 *   - filter-pill label/count text (driven by --accent directly)
 *
 * Against the fixed dark canvas (--bg: #0c1318) a dark brand color collapses
 * into the background and these thin/small elements disappear. `liftForDarkCanvas`
 * returns a lightened variant ONLY when the input fails a minimum contrast ratio
 * against the canvas; otherwise it returns `undefined` so the caller keeps the
 * existing derivation untouched (bright/mid brands are never altered). Non-hex
 * inputs (rgba/hsl/named) also return `undefined` — we can't safely measure them,
 * so the author stays in control.
 *
 * The color is mixed toward white in small steps, preserving hue as much as
 * possible, until it clears the floor.
 */

const CANVAS_BG = "#0c1318";
const WHITE = { r: 255, g: 255, b: 255 };

interface Rgb {
  r: number;
  g: number;
  b: number;
}

const parseHex = (hex: string): Rgb | null => {
  if (typeof hex !== "string") return null;
  let h = hex.trim().replace(/^#/, "");
  if (h.length === 3) {
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  }
  if (!/^[0-9a-f]{6}$/i.test(h)) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
};

const toHexChannel = (n: number): string =>
  Math.round(Math.max(0, Math.min(255, n)))
    .toString(16)
    .padStart(2, "0");

const rgbToHex = ({ r, g, b }: Rgb): string =>
  `#${toHexChannel(r)}${toHexChannel(g)}${toHexChannel(b)}`;

/** WCAG relative luminance (0–1). */
const luminance = ({ r, g, b }: Rgb): number => {
  const lin = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
};

/** WCAG contrast ratio between two colors (1–21). */
const contrastRatio = (a: Rgb, b: Rgb): number => {
  const la = luminance(a);
  const lb = luminance(b);
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
};

/** Linear interpolate `a` toward `b` by `t` (0–1). */
const mix = (a: Rgb, b: Rgb, t: number): Rgb => ({
  r: a.r + (b.r - a.r) * t,
  g: a.g + (b.g - a.g) * t,
  b: a.b + (b.b - a.b) * t,
});

/**
 * Returns a lightened hex if `color` is too dark to read against the canvas,
 * otherwise `undefined` (= leave the existing styling alone).
 *
 * @param minContrast WCAG ratio floor. 3 ≈ UI-component bar (1.4.11), good for
 *   borders; bump toward 4.5 for small text.
 */
export const liftForDarkCanvas = (
  color: string | undefined,
  minContrast = 3,
): string | undefined => {
  if (!color) return undefined;
  const rgb = parseHex(color);
  if (!rgb) return undefined;
  const bg = parseHex(CANVAS_BG) as Rgb;
  if (contrastRatio(rgb, bg) >= minContrast) return undefined;
  for (let t = 0.05; t < 1; t += 0.05) {
    const lifted = mix(rgb, WHITE, t);
    if (contrastRatio(lifted, bg) >= minContrast) return rgbToHex(lifted);
  }
  return rgbToHex(WHITE);
};
